import {
  fetchAdminParallels,
  type AdminParallelSession,
} from "@/api/admin";
import { AdminMediaPreview } from "@/components/admin/AdminMediaPreview";
import { AdminCard } from "@/components/admin/AdminShell";
import { normalizeMediaUrl } from "@/lib/media";
import { Button } from "@/components/ui/Button";
import { useEffect, useState } from "react";

type Props = {
  search: string;
  status: string;
  refreshKey: number;
  busy: boolean;
  onSearch: () => void;
  onSearchChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onDelete: (sessionId: string) => void;
  onDeleteAll: () => void;
};

export function AdminParallelsTab({
  search,
  status,
  refreshKey,
  busy,
  onSearch,
  onSearchChange,
  onStatusChange,
  onDelete,
  onDeleteAll,
}: Props) {
  const [sessions, setSessions] = useState<AdminParallelSession[]>([]);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    void fetchAdminParallels(search, status || undefined).then(setSessions).catch(() => undefined);
  }, [search, status, refreshKey]);

  async function handleDelete(session: AdminParallelSession) {
    if (!window.confirm("Remove this parallel session?")) return;
    setRemovingIds((prev) => new Set(prev).add(session.id));
    await new Promise((r) => window.setTimeout(r, 320));
    onDelete(session.id);
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(session.id);
      return next;
    });
  }

  return (
    <AdminCard
      title="Parallel sessions"
      action={
        <Button variant="outline" disabled={busy} onClick={onDeleteAll}>
          Delete all
        </Button>
      }
    >
      <section className="space-y-4">
        <p className="text-xs text-ink-muted">
          Pending sessions and rows with no capture media are auto-removed when you open Frames, Drifts, or
          Parallels.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            className="min-w-[220px] flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
            placeholder="Search session id, username, caption…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <select
            className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="invited">Invited</option>
            <option value="countdown">Countdown</option>
            <option value="capturing">Capturing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Button onClick={onSearch} disabled={busy}>
            Search
          </Button>
        </div>

        <ul className="space-y-3">
          {sessions.map((s) => {
            const thumb = s.thumbnail_url ? normalizeMediaUrl(s.thumbnail_url) : null;
            const isPending = ["invited", "countdown", "capturing"].includes(s.status);
            return (
              <li
                key={s.id}
                className={[
                  "rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm",
                  removingIds.has(s.id) ? "admin-row-exit" : "",
                ].join(" ")}
              >
                <div className="flex gap-3">
                  <AdminMediaPreview
                    post={{
                      thumbnail_url: thumb,
                      media_urls: thumb ? [thumb] : [],
                    }}
                    tall
                    variant="image"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="font-medium capitalize">
                          {s.status} · {s.member_count ?? 2} members · {s.capture_count ?? 0} captures
                          {isPending ? (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                              pending
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-ink-muted">
                          {new Date(s.created_at).toLocaleString()}
                          {s.post_id ? ` · post ${s.post_id.slice(0, 8)}…` : " · no feed post yet"}
                        </div>
                        <p className="mt-1 text-sm">{s.caption || s.member_usernames || "(no caption)"}</p>
                        <p className="mt-1 text-xs text-ink-muted">
                          @{s.user_a_username ?? "—"} + @{s.user_b_username ?? "—"}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() => void handleDelete(s)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {!sessions.length ? (
            <li className="rounded-xl bg-[#f8f9fb] px-4 py-8 text-center text-sm text-ink-muted">
              No parallel sessions found
            </li>
          ) : null}
        </ul>
      </section>
    </AdminCard>
  );
}
